import { api } from "../../api/index.ts";
import { useCallback } from "react";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { extractApiError } from "../../api/errors.ts";
import { LocationEntry } from "../../types/shared.ts";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import {
  locationsCreateToExternal,
  locationsUpdateToExternal,
} from "../../pages/locations/converts.ts";

export const useSetLocationData = () => {
  const showToast = useShowToast();

  const [triggerCreate] =
    api.endpoints.createLocationApiLocationsPost.useMutation();
  const [triggerUpdate] =
    api.endpoints.updateLocationApiLocationsLocationIdPut.useMutation();
  const [triggerDelete] =
    api.endpoints.deleteLocationApiLocationsLocationIdDelete.useMutation();

  const setData = useCallback(
    async (updatedRows: Partial<LocationEntry>[]) => {
      return await Promise.all(
        updatedRows.map((row) => {
          if (row.id) {
            const update = locationsUpdateToExternal(row);
            if (Object.entries(update).length !== 0) {
              return triggerUpdate({
                locationId: row.id,
                locationUpdate: update,
              });
            } else {
              return undefined;
            }
          } else {
            return triggerCreate({
              locationCreate: locationsCreateToExternal(row as LocationEntry),
            });
          }
        }),
      )
        .then((results) => {
          console.info("Update results ", results);
          results.forEach((result) => {
            if (result && "error" in result) {
              showToast({
                severity: "error",
                summary: "Błąd",
                detail: extractApiError(
                  result.error as FetchBaseQueryError | SerializedError,
                ),
              });
            }
          });
          return results;
        })
        .catch((err) => {
          console.error(err);
          return null;
        });
    },
    [triggerCreate, triggerUpdate, showToast],
  );

  const deleteData = useCallback(
    async (locationId: number) => {
      try {
        const result = await triggerDelete({ locationId });
        console.info("Deleted location", locationId, result);
        return result;
      } catch (error) {
        console.error("Delete error", error);
        throw error;
      }
    },
    [triggerDelete],
  );

  return { setData, deleteData };
};
